import { FunctionComponent, memo, useEffect, useState } from "react";
import { IProps } from "./edit-phrase-trigger.types";
import PhraseTriggerForm, { IPhraseTriggerFormValues } from "components-for/phrase-triggers/phrase-trigger-form";
import {
    InteractionType,
    IPhraseTrigger,
    IPhraseTriggerPostRequest,
    Route,
    SearchType,
} from "@edgetier/watchtower-types";
import { useHistory, useParams } from "react-router-dom";
import { Box, BoxContent, BoxHeader, BoxHeading } from "@edgetier/client-components";
import PhraseTriggerSamples from "components-for/phrase-triggers/phrase-trigger-samples";
import { Page, PageContent, PageHeader } from "@edgetier/client-components";
import { useTag } from "hooks-for/tags/use-tag";
import usePhraseTrigger from "hooks-for/phrase-triggers/use-phrase-trigger";
import toast from "react-hot-toast";
import { getServerError } from "utilities/get-server-error";
import { useQueryClient } from "react-query";
import { axios } from "utilities/axios";
import RoutePage from "constants/page";
import urljoin from "url-join";
import { Button, Spinner } from "@edgetier/components";
import { trackUser } from "utilities/track-user";
import { faArrowLeft, faChartBar } from "@fortawesome/pro-solid-svg-icons";
import "./edit-phrase-trigger.scss";

/**
 * A form for editing an existing phrase trigger.
 */
const EditPhraseTrigger: FunctionComponent<IProps> = () => {
    const queryClient = useQueryClient();
    const history = useHistory();

    const { tagId, phraseTriggerId } = useParams<{
        readonly tagId: string | undefined;
        readonly phraseTriggerId: string | undefined;
    }>();

    const [phraseTriggerValues, setPhraseTriggerValues] = useState<IPhraseTriggerFormValues>();
    const [isPhraseTriggerFormLoading, setIsPhraseTriggerFormLoading] = useState<boolean>(false);

    // Get tag details
    const { data: tag } = useTag(tagId!, {
        enabled: typeof tagId === "string",
    });

    // Retrieve phrase details
    const { data: phraseTrigger, isLoading: isPhraseTriggerLoading } = usePhraseTrigger(phraseTriggerId!, {
        enabled: typeof phraseTriggerId === "string",
    });

    useEffect(() => {
        if (typeof phraseTrigger !== "undefined") {
            setPhraseTriggerValues(phraseTrigger);
        }
    }, [phraseTrigger]);

    /**
     * Send a PATCH request to the backend with the new phrase trigger values.
     * @param phraseTriggerFormValues The new phrase trigger values.
     */
    const updatePhraseTrigger = async (phraseTriggerFormValues: IPhraseTriggerFormValues) => {
        if (typeof phraseTrigger !== "undefined") {
            setIsPhraseTriggerFormLoading(true);

            try {
                await axios.patch(
                    Route.PhraseTrigger.replace(":phraseTriggerId", phraseTrigger.phraseTriggerId),
                    phraseTriggerFormValues
                );
                queryClient.setQueriesData<IPhraseTrigger[]>(Route.PhraseTriggers, (phraseTriggers) =>
                    (phraseTriggers ?? []).map((cachedPhraseTrigger) => {
                        if (cachedPhraseTrigger.phraseTriggerId === phraseTrigger.phraseTriggerId) {
                            return { ...cachedPhraseTrigger, ...phraseTriggerFormValues };
                        }
                        return cachedPhraseTrigger;
                    })
                );
                trackUser("Edit Phrase Trigger", { phraseTriggerId });
                toast.success("Successfully edited phrase trigger");
                history.push(urljoin(RoutePage.ManageTags, tag!.tagId));
            } catch (error) {
                toast.error(getServerError(error));
                setIsPhraseTriggerFormLoading(false);
            }
        }
    };

    /**
     * Send a POST request to the backend to create a new phrase trigger.
     * @param phraseTriggerFormValues
     */
    const createPhraseTrigger = async (phraseTriggerFormValues: IPhraseTriggerFormValues) => {
        setIsPhraseTriggerFormLoading(true);

        try {
            const { data } = await axios.post<IPhraseTriggerPostRequest>(Route.PhraseTriggers, phraseTriggerFormValues);
            queryClient.setQueriesData<IPhraseTrigger[]>(Route.PhraseTriggers, (phraseTriggers) => {
                return [...(phraseTriggers ?? []), data];
            });
            toast.success("Successfully created phrase trigger");
            trackUser("Create Phrase Trigger");
            history.push(urljoin(RoutePage.ManageTags, tag!.tagId));
        } catch (error) {
            toast.error(getServerError(error));
            setIsPhraseTriggerFormLoading(false);
        }
    };

    const onSubmit = (phraseTriggerFormValues: IPhraseTriggerFormValues) => {
        if (typeof phraseTrigger !== "undefined") {
            updatePhraseTrigger(phraseTriggerFormValues);
        } else {
            createPhraseTrigger(phraseTriggerFormValues);
        }
    };

    const initialPhraseTrigger = {
        phrase: "",
        tagId: tagId ?? "",
        searchType: SearchType[SearchType.Similarity],
        searchTypeId: SearchType.Similarity,
        similarityThreshold: 1,
        inboundMessages: true,
        outboundMessages: false,
        botMessages: false,
        interactionTypes: [InteractionType.Email, InteractionType.Chat],
        languageIsoCodes: [],
        interactionsTagged: 0,
    };

    return (
        <Page className="edit-phrase-triggers">
            <PageHeader
                title={
                    isPhraseTriggerLoading
                        ? "...loading"
                        : typeof tag === "undefined"
                        ? "Phrase Triggers"
                        : `${tag.tagGroup} / ${tag.tag} / ${
                              typeof phraseTrigger === "undefined" ? "Phrase Trigger" : phraseTrigger.phrase
                          }`
                }
            >
                {typeof phraseTriggerId === "string" && typeof tagId === "string" && (
                    <div className="edit-phrase-triggers__actions">
                        <Button
                            outline
                            styleName="positive"
                            icon={faArrowLeft}
                            onClick={() => {
                                trackUser("Load Trigger Page");
                                history.push(urljoin(RoutePage.ManageTags, tagId));
                            }}
                        >
                            Triggers
                        </Button>
                        <Button
                            styleName="positive"
                            icon={faChartBar}
                            onClick={() => {
                                trackUser("Load Performance Page");
                                history.push(
                                    RoutePage.PhraseTriggersReport.replace(":tagId", tagId).replace(
                                        ":phraseTriggerId",
                                        phraseTriggerId
                                    )
                                );
                            }}
                        >
                            Performance
                        </Button>
                    </div>
                )}
            </PageHeader>
            <PageContent>
                <Box className="edit-phrase-triggers__form">
                    <BoxHeader>
                        <BoxHeading>
                            {typeof phraseTriggerId === "string" ? "Edit Phrase Triggers" : "Create Phrase Triggers"}
                        </BoxHeading>
                    </BoxHeader>

                    <BoxContent>
                        {isPhraseTriggerLoading ? (
                            <Spinner />
                        ) : (
                            <PhraseTriggerForm
                                initialPhraseTrigger={phraseTrigger ?? initialPhraseTrigger}
                                getPreviewResults={(values) => {
                                    setPhraseTriggerValues(values);
                                    trackUser("Load Preview Results");
                                }}
                            />
                        )}
                    </BoxContent>
                </Box>
                {typeof phraseTriggerValues !== "undefined" && (
                    <PhraseTriggerSamples
                        phraseTriggerValues={phraseTriggerValues}
                        tag={tag!}
                        isLoading={isPhraseTriggerFormLoading}
                        submitPhraseTriggerValues={onSubmit}
                    />
                )}
            </PageContent>
        </Page>
    );
};

export default memo(EditPhraseTrigger);
